import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Moon, Sun, HelpCircle, Search } from 'lucide-react';
import { DarkModeContext } from "../context/DarkModeContext";
import { UiSettingsContext } from "../context/UiSettingsContext";
import { UserContext } from "../context/UserContext";
import { useLicense } from "../context/LicenseContext";
import { getVisibleNavItems } from "../config/navigation";
import logo from '../assets/uni-fund-logo-clean.png';
import logoWhite from '../assets/uni-fund-logo-white-outlined.png';
import api from '../utils/api';
import { API_PROXY, API_BASE } from '../utils/env';

const resolveApiBase = () => {
  if (API_PROXY) return API_PROXY;
  if (API_BASE) {
    return API_BASE.replace(/\/api$/, '');
  }
  try {
    const url = new URL(window.location.href);
    return `${url.protocol}//${url.hostname}:4000`;
  } catch (e) {
    return '';
  }
};

const Navbar = () => {
  const { darkMode, toggleDarkMode } = useContext(DarkModeContext);
  const { settings: uiSettings = {} } = useContext(UiSettingsContext);
  const { user, setUser } = useContext(UserContext);
  const { isModuleEnabled } = useLicense();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthed = !!user;
  const roleSlugs = (user?.role_slugs || []).map(s => String(s).toLowerCase());
  const isAdmin = roleSlugs.includes('admin') || roleSlugs.includes('superadmin');
  const canSeeForms = isAdmin || roleSlugs.includes('forms') || roleSlugs.includes('form_admin');
  const primaryRole = roleSlugs[0] || null;
  const displayRole = primaryRole
    ? primaryRole.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : null;

  const [enabledForms, setEnabledForms] = useState([]);
  const [formsFilter, setFormsFilter] = useState('');
  const [formsMenuOpen, setFormsMenuOpen] = useState(false);

  const dropdownRef = useRef(null);
  const formsDropdownRef = useRef(null);
  const moreDropdownRef = useRef(null);
  const moreToggleRef = useRef(null);
  const moreOpenFocusRef = useRef('first');
  const skipLinkId = 'main-content';
  const mobileMenuRef = useRef(null);
  const primaryMenuRef = useRef(null);
  const userToggleRef = useRef(null);
  const formLinkRefs = useRef([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  useEffect(() => {
    if (!canSeeForms) {
      setEnabledForms([]);
      return undefined;
    }
    let isCancelled = false;
    api.get('/form_builder/enabled_forms')
      .then(res => {
        if (!isCancelled) setEnabledForms(res.data || []);
      })
      .catch(() => {
        if (!isCancelled) setEnabledForms([]);
      });
    return () => { isCancelled = true; };
  }, [canSeeForms]);

  useEffect(() => {
    formLinkRefs.current = [];
  }, [enabledForms]);

  useEffect(() => {
    if (!canSeeForms) setFormsMenuOpen(false);
  }, [canSeeForms]);

  useEffect(() => {
    if (menuOpen && typeof document !== 'undefined') {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [menuOpen]);

  useEffect(() => {
    if (formsMenuOpen && enabledForms.length > 0) {
      requestAnimationFrame(() => {
        formLinkRefs.current[0]?.focus();
      });
    }
  }, [formsMenuOpen, enabledForms.length]);

  useEffect(() => {
    function handleClickOutside(event) {
      const isUserDropdown = dropdownRef.current && dropdownRef.current.contains(event.target);
      const isFormsDropdown = formsDropdownRef.current && formsDropdownRef.current.contains(event.target);
      const isMoreDropdown = moreDropdownRef.current && moreDropdownRef.current.contains(event.target);
      if (!isUserDropdown) setDropdownOpen(false);
      if (!isFormsDropdown) setFormsMenuOpen(false);
      if (!isMoreDropdown) setMoreMenuOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!moreMenuOpen) return;
    requestAnimationFrame(() => {
      const items = Array.from(moreDropdownRef.current?.querySelectorAll('#more-menu a, #more-menu button') || []);
      if (!items.length) return;
      const focusTarget = moreOpenFocusRef.current === 'last' ? items[items.length - 1] : items[0];
      focusTarget?.focus();
    });
  }, [moreMenuOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.defaultPrevented) return;
      if (e.key !== 'Escape') return;
      if (!(menuOpen || formsMenuOpen || dropdownOpen || moreMenuOpen)) return;

      const activeEl = document.activeElement;
      const focusTarget = formsDropdownRef.current?.contains(activeEl)
        ? formsDropdownRef.current.querySelector('button')
        : moreDropdownRef.current?.contains(activeEl)
          ? moreToggleRef.current
          : dropdownRef.current?.contains(activeEl)
            ? userToggleRef.current
            : primaryMenuRef.current?.contains(activeEl)
              ? mobileMenuRef.current
              : menuOpen
                ? mobileMenuRef.current
                : formsMenuOpen
                  ? formsDropdownRef.current?.querySelector('button')
                  : dropdownOpen
                    ? userToggleRef.current
                    : moreToggleRef.current;

      setMenuOpen(false);
      setFormsMenuOpen(false);
      setDropdownOpen(false);
      setMoreMenuOpen(false);
      requestAnimationFrame(() => focusTarget?.focus());
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [menuOpen, formsMenuOpen, dropdownOpen, moreMenuOpen]);

  useEffect(() => {
    // Close all menus when navigating
    setMenuOpen(false);
    setFormsMenuOpen(false);
    setDropdownOpen(false);
    setMoreMenuOpen(false);
  }, [location.pathname]);


  const handleLogout = async () => {
    try {
      await api.post('/users/logout', null, { withCredentials: true });
    } catch (e) {
      // ignore network issues; still clear client state
    }
    // Clear client state
    try { localStorage.removeItem('access_token'); } catch (e) {}
    try { delete api.defaults.headers.common.Authorization; } catch (e) {}
    // Clear UserContext so UI immediately reflects logged-out state
    setUser(null);
    navigate('/login', { replace: true });
  };

  const handleProtectedNav = (path, e) => {
    // Allow Home when logged out; send others to login
    if (!isAuthed && path !== '/') {
      e.preventDefault();
      navigate('/login');
      setMenuOpen(false);
      return;
    }
    setMenuOpen(false);
  };

  const handleFormsButtonKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setFormsMenuOpen(true);
      requestAnimationFrame(() => formLinkRefs.current[0]?.focus());
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFormsMenuOpen(true);
      requestAnimationFrame(() => formLinkRefs.current[enabledForms.length - 1]?.focus());
    } else if (e.key === 'Escape') {
      setFormsMenuOpen(false);
    }
  };

  const handleFormsMenuKeyDown = (e) => {
    if (!formsMenuOpen) return;
    const total = enabledForms.length;
    if (!total) return;
    const currentIndex = formLinkRefs.current.findIndex(el => el === document.activeElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = (currentIndex + 1) % total;
      formLinkRefs.current[next]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = (currentIndex - 1 + total) % total;
      formLinkRefs.current[prev]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      formLinkRefs.current[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      formLinkRefs.current[total - 1]?.focus();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setFormsMenuOpen(false);
      formsDropdownRef.current?.querySelector('button')?.focus();
    }
  };

  const handleMoreButtonKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      moreOpenFocusRef.current = 'first';
      setMoreMenuOpen(true);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moreOpenFocusRef.current = 'last';
      setMoreMenuOpen(true);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setMoreMenuOpen(false);
      requestAnimationFrame(() => moreToggleRef.current?.focus());
    }
  };

  const handleMoreMenuKeyDown = (e) => {
    if (!moreMenuOpen) return;
    const items = Array.from(moreDropdownRef.current?.querySelectorAll('#more-menu a, #more-menu button') || []);
    const total = items.length;
    if (!total) return;
    const currentIndex = items.findIndex(el => el === document.activeElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = (currentIndex + 1) % total;
      items[next]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = (currentIndex - 1 + total) % total;
      items[prev]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      items[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      items[total - 1]?.focus();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setMoreMenuOpen(false);
      requestAnimationFrame(() => moreToggleRef.current?.focus());
    }
  };

  const navLinkClass = (isActive, disabled = false) =>
    `nav-link inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm md:text-base font-semibold transition ${
      isActive
        ? 'nav-link-active bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-100 border border-blue-200 dark:border-blue-700'
        : 'text-slate-700 dark:text-slate-300 border border-transparent hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-blue-700 dark:hover:text-blue-200'
    } ${disabled ? 'opacity-60 cursor-pointer' : ''}`;

  const filteredForms = formsFilter.trim()
    ? enabledForms.filter(f =>
        (f.name || '').toLowerCase().includes(formsFilter.toLowerCase()) ||
        (f.table_name || '').toLowerCase().includes(formsFilter.toLowerCase())
      )
    : enabledForms;

  // Get allowed routes from user context (null = all routes allowed)
  const allowedRoutes = user?.allowed_routes || null;

  // Get nav items from centralized navigation config (single source of truth)
  const baseNavItems = useMemo(() => {
    let items = getVisibleNavItems({
      userRoles: roleSlugs,
      isModuleEnabled,
      isAuthenticated: isAuthed,
    });

    // Filter by allowed_routes if the user has route restrictions
    if (allowedRoutes && allowedRoutes.length > 0) {
      const allowedSet = new Set(allowedRoutes);
      items = items.filter(item => {
        // Check if the item's path or any parent path is allowed
        // e.g., if "/research" is allowed, "/research/portfolio" should also work
        return allowedSet.has(item.path) ||
          allowedRoutes.some(route => item.path.startsWith(route + '/')) ||
          allowedRoutes.some(route => route.startsWith(item.path + '/'));
      });
    }

    return items;
  }, [roleSlugs, isModuleEnabled, isAuthed, allowedRoutes]);

  // Get nav_config from UI settings (admin-configured visibility and order)
  const navConfig = uiSettings.nav_config || { items: [], primaryKeys: ['home', 'campus_summary'] };

  // Memoize config processing
  const { primaryLinks, secondaryLinks } = useMemo(() => {
    const navConfigMap = {};
    (navConfig.items || []).forEach(item => {
      navConfigMap[item.key] = item;
    });

    // Get the admin-configured primary keys (items always visible, not in More dropdown)
    const primaryKeySet = new Set(navConfig.primaryKeys || ['home', 'campus_summary']);

    // Merge base items with admin config
    const navItems = baseNavItems.map((item, defaultOrder) => {
      const config = navConfigMap[item.key];
      // Determine if this item is primary based on admin config (overrides hardcoded priority)
      const isPrimary = primaryKeySet.has(item.key);
      return {
        ...item,
        // Admin can hide items
        show: config ? config.visible !== false : true,
        order: config?.order ?? defaultOrder,
        // Override priority based on admin config
        priority: isPrimary ? 'primary' : 'secondary',
      };
    });

    // Sort by admin-configured order
    navItems.sort((a, b) => a.order - b.order);

    const visible = navItems.filter(item => item.show);
    return {
      primaryLinks: visible.filter(item => item.priority === 'primary'),
      secondaryLinks: visible.filter(item => item.priority === 'secondary'),
    };
  }, [baseNavItems, navConfig]);

  const renderNavLink = (item, variant = 'inline') => {
    const isDisabled = item.requiresAuth && !isAuthed;
    if (variant === 'dropdown') {
      return (
        <NavLink
          key={item.path}
          to={item.path}
          role="menuitem"
          data-tour={item.key === 'home' ? 'dashboard-link' : undefined}
          className={({ isActive }) =>
            `block w-full px-3 py-2 rounded-lg text-sm font-semibold transition ${
              isActive
                ? 'bg-blue-100 text-blue-800 dark:bg-blue-800/60 dark:text-blue-50'
                : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
            } ${isDisabled ? 'opacity-60 cursor-pointer' : ''}`
          }
          onClick={(e) => {
            handleProtectedNav(item.path, e);
            setMoreMenuOpen(false);
          }}
        >
          {item.label}
        </NavLink>
      );
    }

    return (
      <NavLink
        key={item.path}
        to={item.path}
        data-tour={item.key === 'home' ? 'dashboard-link' : undefined}
        className={({ isActive }) => navLinkClass(isActive, isDisabled)}
        onClick={(e) => handleProtectedNav(item.path, e)}
      >
        {item.label}
      </NavLink>
    );
  };

  return (
    <>
      <a className="skip-link" href={`#${skipLinkId}`}>Skip to main content</a>
      <nav
        className="navbar-shell sticky top-0 z-50 backdrop-blur py-2 bg-white/85 dark:bg-slate-900/85"
        role="navigation"
        aria-label="Main"
      >
        <div className="nav-shell flex items-center justify-between">
          <NavLink to="/" className="flex items-center space-x-3 h-12">
            <div className="h-12 flex items-center overflow-visible min-w-[140px]">
              {(() => {
              const resolveAssetUrl = (url) => {
                if (!url) return null;
                if (url.startsWith('/static')) {
                  const base = resolveApiBase();
                  return base ? `${base}${url}` : url;
                }
                return url;
              };
              const logoSrc = resolveAssetUrl(uiSettings.branding_logo_url) || (darkMode ? logoWhite : logo);
              const alt = `${uiSettings.branding_app_name || 'Uni-Fund'} logo`;
              const logoScale = uiSettings.branding_logo_scale || 1;
              const baseHeight = 32; // h-8 = 2rem = 32px
              return (
                <img
                  src={logoSrc}
                  alt={alt}
                  className="w-auto"
                  style={{ height: baseHeight * logoScale }}
                />
              );
            })()}
          </div>
        </NavLink>
        <button
          className="md:hidden p-2 min-w-[44px] min-h-[44px] flex flex-col items-center justify-center text-gray-600 dark:text-gray-300 focus:outline-none rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle navigation menu"
          aria-expanded={menuOpen}
          aria-controls="primary-menu"
          ref={mobileMenuRef}
        >
          <span className="sr-only">Open main menu</span>
          <span className="block w-6 h-0.5 bg-current mb-1.5" />
          <span className="block w-6 h-0.5 bg-current mb-1.5" />
          <span className="block w-6 h-0.5 bg-current" />
        </button>
        {/* Mobile menu backdrop */}
        {menuOpen && (
          <div
            className="fixed inset-0 z-[9998] md:hidden bg-black/50"
            aria-hidden="true"
            onClick={() => setMenuOpen(false)}
          />
        )}
        {/* Mobile drawer - slides in from right */}
        <div
          id="primary-menu"
          className={`
            md:flex md:flex-1 md:flex-row md:items-center md:justify-between md:space-y-0 md:ml-4 md:relative md:z-50 md:bg-transparent md:p-0 md:shadow-none md:border-0 md:w-auto md:h-auto md:overflow-visible
            ${menuOpen ? 'flex' : 'hidden md:flex'}
            fixed md:static top-0 right-0 bottom-0 z-[9999]
            w-[85vw] max-w-[320px] md:w-auto md:max-w-none
            flex-col
            bg-white dark:bg-slate-900
            shadow-2xl md:shadow-none
            border-l border-slate-200 dark:border-slate-700 md:border-0
            transform transition-transform duration-300 ease-out
            ${menuOpen ? 'translate-x-0' : 'translate-x-full md:translate-x-0'}
            overflow-hidden md:overflow-visible
          `}
          role={menuOpen ? 'menu' : undefined}
          aria-modal={menuOpen ? 'true' : undefined}
          ref={primaryMenuRef}
          style={menuOpen ? { height: '100dvh' } : undefined}
        >
          {/* Mobile drawer header */}
          <div className="md:hidden flex-shrink-0 flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
            <span className="text-lg font-semibold text-slate-800 dark:text-slate-100">Menu</span>
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Close menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {/* Mobile drawer content - scrollable nav links */}
          <div className="md:hidden flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 space-y-1">
            {/* Primary links */}
            {primaryLinks.map(link => {
              const isDisabled = link.requiresAuth && !isAuthed;
              return (
                <NavLink
                  key={link.path}
                  to={link.path}
                  data-tour={link.key === 'home' ? 'dashboard-link' : undefined}
                  className={({ isActive }) =>
                    `block w-full px-4 py-3 rounded-xl text-base font-semibold transition min-h-[48px] flex items-center ${
                      isActive
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-100'
                        : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
                    } ${isDisabled ? 'opacity-60' : ''}`
                  }
                  onClick={(e) => handleProtectedNav(link.path, e)}
                >
                  {link.label}
                </NavLink>
              );
            })}

            {/* Secondary links */}
            {secondaryLinks.length > 0 && (
              <>
                <div className="border-t border-slate-200 dark:border-slate-700 my-3" />
                <p className="px-4 py-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">More</p>
                {secondaryLinks.map(link => {
                  const isDisabled = link.requiresAuth && !isAuthed;
                  return (
                    <NavLink
                      key={link.path}
                      to={link.path}
                      className={({ isActive }) =>
                        `block w-full px-4 py-3 rounded-xl text-base font-semibold transition min-h-[48px] flex items-center ${
                          isActive
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-100'
                            : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
                        } ${isDisabled ? 'opacity-60' : ''}`
                      }
                      onClick={(e) => handleProtectedNav(link.path, e)}
                    >
                      {link.label}
                    </NavLink>
                  );
                })}
              </>
            )}

            {/* Forms section for mobile */}
            {isAuthed && canSeeForms && enabledForms.length > 0 && (
              <>
                <div className="border-t border-slate-200 dark:border-slate-700 my-3" />
                <p className="px-4 py-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Forms</p>
                <input
                  type="text"
                  value={formsFilter}
                  onChange={(e) => setFormsFilter(e.target.value)}
                  placeholder="Search forms..."
                  className="w-full rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-3 text-base bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 min-h-[48px]"
                />
                {filteredForms.map((form) => (
                  <NavLink
                    key={form.id}
                    to={`/form/${form.table_name}`}
                    className="block w-full px-4 py-3 rounded-xl text-base font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition min-h-[48px] flex items-center"
                    onClick={() => setMenuOpen(false)}
                  >
                    {form.name}
                    <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">({form.table_name})</span>
                  </NavLink>
                ))}
              </>
            )}
          </div>

          {/* Mobile drawer footer - quick actions */}
          <div className="md:hidden flex-shrink-0 border-t border-slate-200 dark:border-slate-700 p-4 space-y-2 bg-slate-50 dark:bg-slate-800/50">
            <div className="flex items-center justify-around">
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  const isMac = navigator.platform.includes('Mac');
                  const event = new KeyboardEvent('keydown', { key: 'k', metaKey: isMac, ctrlKey: !isMac, bubbles: true });
                  document.dispatchEvent(event);
                }}
                className="flex flex-col items-center gap-1 p-3 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition min-w-[64px]"
              >
                <Search className="h-5 w-5 text-slate-600 dark:text-slate-300" />
                <span className="text-xs text-slate-600 dark:text-slate-300">Search</span>
              </button>
              <NavLink
                to="/help"
                onClick={() => setMenuOpen(false)}
                className="flex flex-col items-center gap-1 p-3 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition min-w-[64px]"
              >
                <HelpCircle className="h-5 w-5 text-slate-600 dark:text-slate-300" />
                <span className="text-xs text-slate-600 dark:text-slate-300">Help</span>
              </NavLink>
              <button
                type="button"
                onClick={() => { toggleDarkMode(); setMenuOpen(false); }}
                className="flex flex-col items-center gap-1 p-3 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition min-w-[64px]"
              >
                {darkMode ? <Sun className="h-5 w-5 text-slate-600 dark:text-slate-300" /> : <Moon className="h-5 w-5 text-slate-600 dark:text-slate-300" />}
                <span className="text-xs text-slate-600 dark:text-slate-300">{darkMode ? 'Light' : 'Dark'}</span>
              </button>
              {isAdmin && (
                <NavLink
                  to="/admin"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMenuOpen(false)}
                  className="flex flex-col items-center gap-1 p-3 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition min-w-[64px]"
                >
                  <svg className="h-5 w-5 text-slate-600 dark:text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-xs text-slate-600 dark:text-slate-300">Admin</span>
                </NavLink>
              )}
            </div>
            {user && (
              <div className="flex items-center justify-between pt-2 border-t border-slate-200 dark:border-slate-600">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold">
                    {user.name?.split(' ').map(n => n[0]).join('').toUpperCase() || "?"}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{user.name}</p>
                    {displayRole && <p className="text-xs text-slate-500 dark:text-slate-400">{displayRole}</p>}
                  </div>
                </div>
                <button
                  onClick={() => { handleLogout(); setMenuOpen(false); }}
                  className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition min-h-[44px]"
                >
                  Logout
                </button>
              </div>
            )}
            {!user && (
              <NavLink
                to="/login"
                onClick={() => setMenuOpen(false)}
                className="block w-full text-center px-4 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition min-h-[48px]"
              >
                Sign in
              </NavLink>
            )}
          </div>

          {/* Desktop nav - hidden on mobile */}
          <div className="hidden md:flex md:flex-row md:flex-wrap md:items-center gap-2 md:gap-3 md:pl-2">
            {primaryLinks.map(link => renderNavLink(link))}

            {secondaryLinks.length > 0 && (
              <div className="relative" ref={moreDropdownRef}>
                <button
                  type="button"
                  onClick={() => {
                    moreOpenFocusRef.current = 'first';
                    setMoreMenuOpen(prev => !prev);
                  }}
                  onKeyDown={handleMoreButtonKeyDown}
                  aria-expanded={moreMenuOpen}
                  aria-controls="more-menu"
                  aria-haspopup="menu"
                  ref={moreToggleRef}
                  className="px-3 py-2 text-sm md:text-base font-semibold text-gray-700 dark:text-gray-300 hover:text-blue-700 dark:hover:text-blue-200 rounded-full border border-transparent hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                >
                  More {moreMenuOpen ? '▲' : '▼'}
                </button>
                {moreMenuOpen && (
                  <div
                    id="more-menu"
                    className="surface-card absolute left-0 mt-2 w-60 max-w-[15rem] bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-50 rounded-xl shadow-lg border border-slate-200/80 dark:border-slate-700/60 p-2 space-y-1 max-h-[70vh] overflow-y-auto overscroll-contain"
                    role="menu"
                    onKeyDown={handleMoreMenuKeyDown}
                  >
                    {secondaryLinks.map(link => renderNavLink(link, 'dropdown'))}
                  </div>
                )}
              </div>
            )}

            {/* Forms dropdown - desktop only */}
            {isAuthed && canSeeForms && (
              <div className="relative" ref={formsDropdownRef}>
                <button
                  type="button"
                  onClick={() => setFormsMenuOpen(prev => !prev)}
                  aria-expanded={formsMenuOpen}
                  aria-controls="forms-menu"
                  aria-haspopup="menu"
                  onKeyDown={handleFormsButtonKeyDown}
                  className="px-3 py-2 text-sm md:text-base font-semibold text-gray-700 dark:text-gray-300 hover:text-blue-700 dark:hover:text-blue-300 rounded-full cursor-pointer w-full text-left border border-transparent hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                >
                  Forms {formsMenuOpen ? '▲' : '▼'}
                </button>
                {formsMenuOpen && (
                  <div
                    id="forms-menu"
                    className="surface-card absolute left-0 mt-2 w-64 max-w-xs bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-50 rounded-xl shadow-lg border border-slate-200/80 dark:border-slate-700/60 p-3 space-y-2 max-h-[70vh] overflow-y-auto overscroll-contain"
                    role="menu"
                    onKeyDown={handleFormsMenuKeyDown}
                  >
                    <input
                      type="text"
                      value={formsFilter}
                      onChange={(e) => setFormsFilter(e.target.value)}
                      placeholder="Search forms"
                      className="w-full rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100"
                    />
                    {filteredForms.length > 0 ? (
                      filteredForms.map((form, index) => (
                        <NavLink
                          key={form.id}
                          to={`/form/${form.table_name}`}
                          className="block w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-blue-100 dark:hover:bg-blue-600 transition rounded-lg"
                          onClick={() => setFormsMenuOpen(false)}
                          ref={(el) => { formLinkRefs.current[index] = el; }}
                          role="menuitem"
                        >
                          <span className="font-semibold">{form.name}</span>
                          <span className="ml-1 text-xs text-slate-500 dark:text-slate-300">({form.table_name})</span>
                        </NavLink>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-sm italic text-gray-500 dark:text-gray-400">No forms available</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right: admin link + user menu - desktop only */}
          <div className="hidden md:flex items-center space-x-3 md:space-x-4 md:pl-6 justify-end">
            {/* Search button - triggers global search */}
            <button
              type="button"
              onClick={() => {
                const isMac = navigator.platform.includes('Mac');
                const event = new KeyboardEvent('keydown', {
                  key: 'k',
                  metaKey: isMac,
                  ctrlKey: !isMac,
                  bubbles: true,
                });
                document.dispatchEvent(event);
              }}
              className="relative group inline-flex items-center justify-center w-10 h-10 rounded-full border border-transparent text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-blue-600 dark:hover:text-blue-400 transition"
              aria-label="Search"
              data-tour="search-button"
            >
              <Search className="h-5 w-5" />
              {/* Tooltip - appears below */}
              <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 text-xs font-medium text-white bg-slate-800 dark:bg-slate-700 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                Search
                <span className="block text-slate-400 text-[10px]">{navigator.platform?.includes('Mac') ? '⌘' : 'Ctrl+'}K</span>
              </span>
            </button>
            {/* Help icon */}
            <NavLink
              to="/help"
              className="relative group inline-flex items-center justify-center w-10 h-10 rounded-full border border-transparent text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-blue-600 dark:hover:text-blue-400 transition"
              aria-label="Help Center"
            >
              <HelpCircle className="h-5 w-5" />
              {/* Tooltip - appears below */}
              <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 text-xs font-medium text-white bg-slate-800 dark:bg-slate-700 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                Help Center
              </span>
            </NavLink>
            <button
              type="button"
              onClick={() => {
                toggleDarkMode();
                setDropdownOpen(false);
                setFormsMenuOpen(false);
                setMoreMenuOpen(false);
              }}
              className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-transparent text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
              aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            {isAdmin && (
              <NavLink
                to="/admin"
                target="_blank"
                rel="noopener noreferrer"
                data-tour="admin-link"
                className={({ isActive }) => navLinkClass(isActive)}
                onClick={() => setMenuOpen(false)}
              >
                Admin
              </NavLink>
            )}
            {!user && (
              <NavLink
                to="/login"
                className={({ isActive }) => navLinkClass(isActive)}
                onClick={() => setMenuOpen(false)}
              >
                Sign in
              </NavLink>
            )}
            {user && (
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="cursor-pointer px-3 py-2 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 flex items-center justify-center w-10 h-10 font-bold text-lg"
                  aria-expanded={dropdownOpen}
                  aria-label="Open user menu"
                  ref={userToggleRef}
                >
                  {user.name?.split(' ').map(n => n[0]).join('').toUpperCase() || "?"}
                </button>
                {dropdownOpen && (
                  <ul className="surface-card absolute right-0 mt-2 w-[calc(100vw-2rem)] sm:w-52 max-w-[13rem] bg-white dark:bg-gray-800 border rounded shadow-lg z-50 max-h-[70vh] overflow-y-auto overscroll-contain">
                    <li className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {user.name || 'User'} {displayRole ? `• ${displayRole}` : ''}
                    </li>
                    <li>
                      <NavLink to="/account" className="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200">
                        Account Settings
                      </NavLink>
                    </li>
                    <li>
                      <NavLink to="/" className="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200">
                        My Tasks
                      </NavLink>
                    </li>
                    <li>
                      <button
                        onClick={() => {
                          toggleDarkMode();
                          setDropdownOpen(false);
                        }}
                        className="btn-ghost w-full text-left block"
                      >
                        {darkMode ? "Switch to Light" : "Switch to Dark"}
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left block px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200"
                      >
                        Logout
                      </button>
                    </li>
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      </nav>
    </>
  );
};

export default Navbar;
